import { useEffect, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { toast } from 'sonner';
import { BookOpen, Code2, Copy, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Switch } from '@/components/atoms/switch';
import { Badge } from '@/components/atoms/badge';
import { Textarea } from '@/components/atoms/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { useBookableServices } from '@/hooks/bookingConfig/useBookableServices';
import { useBookingLinkEmbed } from '@/hooks/bookingConfig/useBookingLinkEmbed';
import { BookingInstallHelpDialog } from '@/components/booking/BookingInstallHelpDialog';
import {
  useBookingLinks,
  useCreateBookingLink,
  useDeleteBookingLink,
  useUpdateBookingLink,
} from '@/hooks/bookingConfig/useBookingLinks';
import type { BookingLink } from '@/types/bookingConfig';

// Folded into the unified Online Booking section; keep the path as a redirect
// so old links/bookmarks land on the Links tab's new home.
export const Route = createFileRoute('/_dashboard/dashboard/settings/booking-links')({
  beforeLoad: () => {
    throw redirect({
      to: '/dashboard/settings/online-booking',
      search: { tab: 'links' },
    });
  },
});

const publicUrl = (key: string) =>
  `${typeof window !== 'undefined' ? window.location.origin : ''}/onlinebooking/${key}`;

/**
 * Booking-links management. `embedded` drops the standalone page chrome so it
 * can live inside the unified Online Booking tabs.
 */
export function BookingLinksPanel({ embedded = false }: { embedded?: boolean }) {
  const { data: links = [], isLoading } = useBookingLinks();
  const remove = useDeleteBookingLink();
  const [editing, setEditing] = useState<BookingLink | null>(null);
  const [open, setOpen] = useState(false);
  const [embedFor, setEmbedFor] = useState<BookingLink | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const copy = (key: string) => {
    void navigator.clipboard.writeText(publicUrl(key));
    toast.success('Link copied');
  };

  return (
    <div className={embedded ? 'space-y-4' : 'mx-auto max-w-4xl space-y-6 p-6'}>
      <header className="flex items-center justify-between">
        <div>
          {!embedded && <h1 className="text-xl font-semibold">Booking Links</h1>}
          <p className="text-sm text-muted-foreground">
            Shareable links where customers pick a service and book online.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setHelpOpen(true)}>
            <BookOpen className="mr-1 h-3.5 w-3.5" />
            How to install
          </Button>
          <Button
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus className="mr-1 h-4 w-4" /> New link
          </Button>
        </div>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : links.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No booking links yet. Create one and choose which services it offers.
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {links.map((link) => (
            <li key={link.id} className="space-y-2 p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{link.name}</span>
                  {!link.isActive && <Badge variant="secondary">Paused</Badge>}
                  <Badge variant="outline">
                    {link.services.length} service
                    {link.services.length === 1 ? '' : 's'}
                  </Badge>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copy(link.key)}
                    aria-label="Copy link"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => window.open(publicUrl(link.key), '_blank')}
                    aria-label="Open link"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEmbedFor(link)}
                    aria-label="Embed code"
                  >
                    <Code2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditing(link);
                      setOpen(true);
                    }}
                    aria-label="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm(`Remove "${link.name}"?`)) remove.mutate(link.id);
                    }}
                    aria-label="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <code className="block truncate rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                {publicUrl(link.key)}
              </code>
            </li>
          ))}
        </ul>
      )}

      <LinkFormDialog open={open} link={editing} onClose={() => setOpen(false)} />
      <EmbedDialog link={embedFor} onClose={() => setEmbedFor(null)} />
      <BookingInstallHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}

/** The copy-paste widget snippet for embedding this link on a customer site. */
function EmbedDialog({ link, onClose }: { link: BookingLink | null; onClose: () => void }) {
  const { data, isLoading } = useBookingLinkEmbed(link?.id ?? null);
  const snippet = data?.snippet ?? '';
  return (
    <Dialog open={Boolean(link)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Embed “{link?.name}”</DialogTitle>
          <DialogDescription>
            Paste this snippet just before the closing &lt;/body&gt; tag. It adds a “Book now”
            launcher that opens this booking page in an overlay. To use your own button instead, add{' '}
            <code className="text-xs">mode: "manual"</code> to the settings and call{' '}
            <code className="text-xs">window.NotifiBooking('open')</code> (or put a{' '}
            <code className="text-xs">data-notifi-booking</code> attribute on any element).
          </DialogDescription>
        </DialogHeader>
        <Textarea
          readOnly
          value={isLoading ? 'Loading…' : snippet}
          rows={6}
          className="font-mono text-xs"
          onFocus={(e) => e.currentTarget.select()}
        />
        <div className="flex justify-end">
          <Button
            onClick={() => {
              void navigator.clipboard.writeText(snippet);
              toast.success('Snippet copied');
            }}
            disabled={!snippet}
          >
            <Copy className="mr-1 h-4 w-4" />
            Copy snippet
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinkFormDialog({
  open,
  link,
  onClose,
}: {
  open: boolean;
  link: BookingLink | null;
  onClose: () => void;
}) {
  const { data: services = [] } = useBookableServices();
  const create = useCreateBookingLink();
  const update = useUpdateBookingLink();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  // Appearance knobs stored in the link's opaque config JSON.
  const [themeColor, setThemeColor] = useState('');
  const [headline, setHeadline] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [position, setPosition] = useState('bottom-right');
  const [showPrices, setShowPrices] = useState(true);

  useEffect(() => {
    if (!open) return;
    setName(link?.name ?? '');
    setIsActive(link?.isActive ?? true);
    setSelected(link?.services.map((s) => s.bookableServiceId) ?? []);
    const cfg = link?.config ?? {};
    setThemeColor(typeof cfg.themeColor === 'string' ? cfg.themeColor : '');
    setHeadline(typeof cfg.headline === 'string' ? cfg.headline : '');
    setWelcomeMessage(typeof cfg.welcomeMessage === 'string' ? cfg.welcomeMessage : '');
    setButtonText(typeof cfg.buttonText === 'string' ? cfg.buttonText : '');
    setPosition(cfg.position === 'bottom-left' ? 'bottom-left' : 'bottom-right');
    setShowPrices(cfg.showPrices !== 'false');
  }, [open, link]);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = () => {
    // Merge onto the existing config so keys this form doesn't know about
    // survive (webchat-style non-destructive merge).
    const config = { ...(link?.config ?? {}) };
    const setOrDelete = (key: string, value: string | undefined) => {
      if (value) config[key] = value;
      else delete config[key];
    };
    setOrDelete('themeColor', themeColor.trim() || undefined);
    setOrDelete('headline', headline.trim() || undefined);
    setOrDelete('welcomeMessage', welcomeMessage.trim() || undefined);
    setOrDelete('buttonText', buttonText.trim() || undefined);
    setOrDelete('position', position === 'bottom-left' ? 'bottom-left' : undefined);
    setOrDelete('showPrices', showPrices ? undefined : 'false');

    const dto = {
      name: name.trim(),
      bookableServiceIds: selected,
      isActive,
      config,
    };
    const onDone = { onSuccess: onClose };
    if (link) {
      update.mutate({ id: link.id, dto }, onDone);
    } else {
      create.mutate(dto, onDone);
    }
  };

  const saving = create.isPending || update.isPending;
  const activeServices = services.filter((s) => s.isActive || selected.includes(s.id));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{link ? 'Edit link' : 'New booking link'}</DialogTitle>
          <DialogDescription>
            Choose which services customers can book on this link.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="bl-name">Name</Label>
            <Input
              id="bl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Main Site Booking"
            />
          </div>

          <div className="space-y-2">
            <Label>Services on this link</Label>
            {activeServices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No bookable services yet — create some first.
              </p>
            ) : (
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-2">
                {activeServices.map((svc) => (
                  <label
                    key={svc.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={selected.includes(svc.id)}
                      onChange={() => toggle(svc.id)}
                    />
                    <span className="text-sm">{svc.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} id="bl-active" />
            <Label htmlFor="bl-active">Active (link accepts bookings)</Label>
          </div>

          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Appearance</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="bl-theme">Theme color</Label>
                <Input
                  id="bl-theme"
                  value={themeColor}
                  onChange={(e) => setThemeColor(e.target.value)}
                  placeholder="#4f46e5"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bl-button">Launcher button text</Label>
                <Input
                  id="bl-button"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  placeholder="Book now"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bl-headline">Headline</Label>
              <Input
                id="bl-headline"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="Book a service online"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bl-welcome">Welcome message</Label>
              <Textarea
                id="bl-welcome"
                rows={2}
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
                placeholder="Optional intro shown above the service list."
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={showPrices} onCheckedChange={setShowPrices} id="bl-prices" />
                <Label htmlFor="bl-prices">Show prices</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="bl-pos" className="text-muted-foreground">
                  Launcher corner
                </Label>
                <select
                  id="bl-pos"
                  className="h-9 rounded-md border bg-transparent px-2 text-sm"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                >
                  <option value="bottom-right">Bottom right</option>
                  <option value="bottom-left">Bottom left</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || saving}>
            {saving ? 'Saving…' : link ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
